import { RBPayrollFieldErrors, RBPayrollSetupDraft } from "../types/rbPayrollOwner.types";
import { normalizeDigits } from "../utils/rbPayrollOwner.formatters";

export const hasRBPayrollValidationErrors = (errors: RBPayrollFieldErrors) =>
  Object.values(errors || {}).some((sectionValue) => {
    if (!sectionValue || typeof sectionValue !== "object") {
      return Boolean(sectionValue);
    }

    return Object.values(sectionValue).some(Boolean);
  });

// Requirement: an owner removes their banking details by entering all-zero values
// in EVERY field (zeros in institution, transit and account, then Save). A cleared
// section skips the field / document / authorization requirements so the removal
// can be persisted — and reads as complete on the checklist. Leaving fields EMPTY
// is NOT a removal: empty produces "required" errors and never marks complete.
export const isRBPayrollDirectDepositCleared = (
  directDeposit: RBPayrollSetupDraft["directDeposit"]
) => {
  const institutionNumber = normalizeDigits(directDeposit.institutionNumber);
  const transitNumber = normalizeDigits(directDeposit.transitNumber);
  const accountNumber = normalizeDigits(directDeposit.accountNumber);
  const allFieldsFilled =
    institutionNumber.length > 0 && transitNumber.length > 0 && accountNumber.length > 0;
  return allFieldsFilled && /^0+$/.test(institutionNumber + transitNumber + accountNumber);
};

export const validateRBPayrollSetup = (state: RBPayrollSetupDraft): RBPayrollFieldErrors => {
  const errors: RBPayrollFieldErrors = {};

  // Blank / all-zero banking is a valid "remove my details" save.
  if (isRBPayrollDirectDepositCleared(state.directDeposit)) {
    return errors;
  }

  const institutionNumber = normalizeDigits(state.directDeposit.institutionNumber);
  const transitNumber = normalizeDigits(state.directDeposit.transitNumber);
  const accountNumber = normalizeDigits(state.directDeposit.accountNumber);

  if (!institutionNumber) {
    errors.directDeposit = {
      ...(errors.directDeposit || {}),
      institutionNumber: "Institution number is required",
    };
  } else if (institutionNumber.length !== 3) {
    errors.directDeposit = {
      ...(errors.directDeposit || {}),
      institutionNumber: "Institution number must be exactly 3 digits",
    };
  }

  if (!transitNumber) {
    errors.directDeposit = {
      ...(errors.directDeposit || {}),
      transitNumber: "Transit number is required",
    };
  } else if (transitNumber.length !== 5) {
    errors.directDeposit = {
      ...(errors.directDeposit || {}),
      transitNumber: "Transit number must be exactly 5 digits",
    };
  }

  if (!accountNumber) {
    errors.directDeposit = {
      ...(errors.directDeposit || {}),
      accountNumber: "Account number is required",
    };
  } else if (accountNumber.length < 7 || accountNumber.length > 12) {
    errors.directDeposit = {
      ...(errors.directDeposit || {}),
      accountNumber: "Account number must be between 7 and 12 digits",
    };
  }

  if (!state.directDeposit.document) {
    errors.directDeposit = {
      ...(errors.directDeposit || {}),
      document: "A banking document is required.",
    };
  }

  if (!state.directDeposit.authorizationAccepted) {
    errors.directDeposit = {
      ...(errors.directDeposit || {}),
      authorizationAccepted: "Authorization is required before saving RB Pay setup.",
    };
  }

  return errors;
};
